import { useQuery } from "@tanstack/react-query";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Bar, Line } from "react-chartjs-2";
import {
  getInsightsSampsOverTimeByOutlet,
  getInsightsStampTotDist,
} from "../../actions/acc_actions";
import Loading from "../../utils/Loading";

// Register Chart.js components
ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
);

const StampsChart = ({ dateFrom, dateTo, selectedSchema }) => {
  const { isLoading: stampsLoading, data: stampsData } = useQuery({
    queryKey: [
      "insightSampsOverTimeByOutlet",
      selectedSchema,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      getInsightsSampsOverTimeByOutlet({
        to: dateTo,
        from: dateFrom,
        refresh: false,
        loyalty_scheme_slug: selectedSchema,
      }),
    enabled: !!dateTo && !!dateFrom && !!selectedSchema,
    keepPreviousData: true,
  });

  const { isLoading: stampsTotLoading, data: stampsTotData } = useQuery({
    queryKey: ["insightStampTotDist", selectedSchema, dateFrom, dateTo],
    queryFn: () =>
      getInsightsStampTotDist({
        refresh: false,
        loyalty_scheme_slug: selectedSchema,
      }),
    enabled: !!dateTo && !!dateFrom && !!selectedSchema,
    keepPreviousData: true,
  });

  if (stampsLoading || stampsTotLoading) {
    return <Loading />;
  }

  // Extract all dates in order (assuming all outlets have same date range)
  const allDates =
    stampsData?.body?.series?.[0]?.data?.map((d) => d.event_date) || [];

  // Generate unique HSL color per outlet index
  const getColor = (index) => {
    const hue = (index * 137.508) % 360; // golden angle for good separation
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Build datasets for each outlet
  const datasets =
    stampsData?.body?.series?.map((outlet, idx) => ({
      label: outlet.outlet_name,
      data: outlet.data.map((d) => d.number_of_stamps),
      borderColor: getColor(idx),
      backgroundColor: getColor(idx) + "33", // 20% opacity
      fill: false,
      tension: 0.4,
    })) || [];

  const timeChartConfig = {
    labels: allDates,
    datasets,
  };

  const timeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
          displayFormats: { day: "MMM dd" },
        },
        min: dateFrom,
        max: dateTo,
        title: {
          display: true,
          text: "Date",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Stamps",
        },
      },
    },
    plugins: {
      legend: { position: "top" },
      tooltip: { mode: "index", intersect: false },
    },
  };

  // Distribution chart
  const distChartConfig = {
    labels: [],
    datasets: [
      {
        label: "Number of Customers",
        data: [],
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  const distChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "category",
        title: {
          display: true,
          text: "Stamp Total",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Customers",
        },
      },
    },
    plugins: {
      legend: { position: "top" },
      tooltip: { mode: "index", intersect: false },
    },
  };

  const distChartData = stampsTotData?.body?.data;
  if (Array.isArray(distChartData) && distChartData.length > 0) {
    distChartConfig.labels = distChartData.map((item) =>
      item.stamp_total.toString(),
    );
    distChartConfig.datasets[0].data = distChartData.map(
      (item) => item.number_of_customers,
    );
  }

  if (!datasets.length && !distChartData?.length) {
    return <div />;
  }

  return (
    <div className="chart">
      <h3>Stamps and Rewards Data</h3>
      <div className="charts-wrapper">
        <div className="chart-item">
          <h4>Stamps Over Time By Outlet</h4>
          <div className="chart-container">
            <Line data={timeChartConfig} options={timeChartOptions} />
          </div>
        </div>
        <div className="chart-item">
          <h4>Stamp Total Distribution</h4>
          <div className="chart-container">
            <Bar data={distChartConfig} options={distChartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StampsChart;
